import { ReactNode, useContext, useEffect, useId, useState } from "react";
import { ThemeWrapper, OverflowTooltip, useMobileLayout } from "@verity/components";
import {
	CoreButton,
	CoreModal,
	CoreModalDescription,
	CoreSearchField,
	CoreSearchFieldPaper,
	CoreTable,
	CoreTableColumn,
	CoreTitleCard,
} from "@verity/components/core";
import apiClient from "../../helpers/api-client";
import i18n from "../../i18n";
import { PortalControllerContext } from "../context";
import BackButton from "../../components/back-button";
import styles from "./select-course-modal.module.css";

type Course = {
	id: number;
	name?: string;
};

export default function SelectCourseModal({
	onClose,
	onSelect,
	title,
	description,
	sortById = false,
}: {
	onClose: () => void;
	onSelect: (id: number) => void;
	title?: ReactNode;
	description?: ReactNode;
	sortById?: boolean;
}) {
	const { portalContext } = useContext(PortalControllerContext);
	const { instituteId } = portalContext;
	const isMobileLayout = useMobileLayout();

	const [courses, setCourses] = useState<Course[]>([]);
	const [loading, setLoading] = useState(false);

	const [searchText, setSearchText] = useState("");

	useEffect(() => {
		(async () => {
			if (instituteId != null) {
				try {
					setLoading(true);
					setCourses(
						(await apiClient.course.getInstituteCoursesInfo(instituteId)).map(({ id, name }) => ({
							id,
							name: name ?? undefined,
						})),
					);
				} finally {
					setLoading(false);
				}
			}
		})();
	}, [instituteId]);

	const displayTitle = title ?? i18n.t("portal.components.selectCourseModalTSX.title");

	const lowerSearchText = searchText?.toLowerCase();
	const filteredCourses = lowerSearchText
		? courses.filter((s) => s.name?.toLowerCase().includes(lowerSearchText))
		: courses;

	const tableId = useId();

	const columns: CoreTableColumn<Course>[] = [
		{
			header: i18n.t("portal.components.selectCourseModalTSX.id"),
			key: "id",
			width: "20%",
			truncateString: true,
			cell: (row) => row.data.id,
			descendingComparator: (a, b) => (a.id! < b.id! ? 1 : -1),
		},
		{
			header: i18n.t("portal.components.selectCourseModalTSX.name"),
			key: "name",
			width: "80%",
			truncateString: true,
			cell: (row) => (
				<OverflowTooltip>
					<span className={styles.courseName}>{row.data.name}</span>
				</OverflowTooltip>
			),
			descendingComparator: (a, b) => (b?.name ?? "").toLowerCase().localeCompare((a?.name ?? "").toLowerCase()),
		},
	];

	return (
		<>
			<CoreModal
				open
				onClose={onClose}
				title={isMobileLayout ? undefined : displayTitle}
				buttons={
					isMobileLayout ? undefined : (
						<CoreButton color="secondary" onClick={onClose}>
							{i18n.t("portal.components.selectCourseModalTSX.cancel")}
						</CoreButton>
					)
				}
				fullScreen={isMobileLayout}
				showTopCloseButton={!isMobileLayout}
			>
				<ThemeWrapper scopedCssBaselineProps={{ className: styles.content }}>
					{isMobileLayout && (
						<CoreTitleCard
							title={displayTitle}
							description={description}
							topContent={<BackButton onBack={onClose} />}
							style={{ minWidth: "300px", padding: "5px 10px 10px 10px", marginTop: "10px" }}
							slotProps={{ text: { style: { gap: "5px" } } }}
						/>
					)}
					{!isMobileLayout && description && <CoreModalDescription>{description}</CoreModalDescription>}
					<CoreSearchFieldPaper>
						<CoreSearchField
							showSearchButton
							label={i18n.t("portal.components.selectCourseModalTSX.search")}
							value={searchText}
							onSearch={setSearchText}
							ariaControls={tableId}
							totalResults={filteredCourses.length}
						/>
					</CoreSearchFieldPaper>
					<CoreTable
						hover
						id={tableId}
						data={filteredCourses}
						columns={columns}
						rowsPerPage={6}
						loading={loading}
						noResultsString={i18n.t("portal.components.selectCourseModalTSX.noResults")}
						initialSortKey={sortById ? "id" : undefined}
						rowProps={{ className: styles.tableRow }}
						onRowClick={(row) => {
							onSelect(row.data.id);
							onClose();
						}}
					/>
				</ThemeWrapper>
			</CoreModal>
		</>
	);
}
